
import { WorldCountry } from '../../types';
import { generateRealisticCityNames } from './cityNameGenerator';
import { generateCitySEOContent } from './seoTemplateGenerator';
import { getExpansionStats } from '../../countryValidator';

export interface MegaPopulationConfig {
  minPopulation: number;
  targetCitiesPerCountry: number;
  batchSize: number;
  generateSEOContent: boolean;
}

export interface PopulationExpansionResult {
  phase: string;
  countriesProcessed: Array<{
    name: string;
    slug: string;
    population: number;
    initialCities: number;
    finalCities: number;
    citiesAdded: number;
  }>;
  totalCitiesGenerated: number;
  processingTime: number;
  eligibleCountries: number;
}

/**
 * Motor de expansión para países con alta población (40M+ habitantes)
 * Objetivo: Llevar cada país elegible a 1000+ ciudades
 */
export class MegaPopulationExpansion {
  private config: MegaPopulationConfig;
  
  constructor(config: Partial<MegaPopulationConfig> = {}) {
    this.config = {
      minPopulation: 40000000, // 40 millones
      targetCitiesPerCountry: 1000,
      batchSize: 50, // Ciudades por lote
      generateSEOContent: true,
      ...config
    };
  }

  /**
   * Identifica países elegibles (40M+ habitantes)
   */
  getEligibleCountries(countries: WorldCountry[]): WorldCountry[] {
    return countries.filter(country => 
      country.population >= this.config.minPopulation
    ).sort((a, b) => b.population - a.population); // Ordenar por población descendente
  }

  /**
   * Ejecuta la mega expansión para países poblados
   */
  async executeMegaPopulationExpansion(countries: WorldCountry[]): Promise<PopulationExpansionResult> {
    const startTime = Date.now();
    
    console.log('\n' + '🌍'.repeat(25));
    console.log('🌍 MEGA EXPANSIÓN PARA PAÍSES POBLADOS');
    console.log('🌍 Criterio: 40+ millones de habitantes');
    console.log('🌍 Objetivo: 1000 ciudades por país');
    console.log('🌍'.repeat(25) + '\n');

    // Identificar países elegibles
    const eligibleCountries = this.getEligibleCountries(countries);
    
    console.log(`📊 PAÍSES ELEGIBLES (40M+ habitantes): ${eligibleCountries.length}`);
    console.log('📋 Lista de países a expandir:');
    eligibleCountries.forEach((country, index) => {
      const currentCities = country.topCities.length;
      const needed = Math.max(0, this.config.targetCitiesPerCountry - currentCities);
      console.log(`   ${index + 1}. ${country.name}: ${country.population.toLocaleString()} hab, ${currentCities} ciudades (necesita ${needed})`);
    });

    if (eligibleCountries.length === 0) {
      console.log('❌ No hay países que cumplan el criterio de población');
      return {
        phase: 'MegaPopulation',
        countriesProcessed: [],
        totalCitiesGenerated: 0,
        processingTime: Date.now() - startTime,
        eligibleCountries: 0
      };
    }

    const processedCountries = [];
    let totalCitiesGenerated = 0;

    // Procesar cada país elegible
    for (const country of eligibleCountries) {
      const initialCities = country.topCities.length;
      const neededCities = Math.max(0, this.config.targetCitiesPerCountry - initialCities);
      
      if (neededCities === 0) {
        console.log(`✅ ${country.name} ya tiene ${initialCities} ciudades (objetivo alcanzado)`);
        processedCountries.push({
          name: country.name,
          slug: country.slug,
          population: country.population,
          initialCities,
          finalCities: initialCities,
          citiesAdded: 0
        });
        continue;
      }

      console.log(`\n🔥 EXPANDIENDO ${country.name.toUpperCase()}`);
      console.log(`   👥 Población: ${country.population.toLocaleString()}`);
      console.log(`   🏙️ Ciudades actuales: ${initialCities}`);
      console.log(`   🎯 Ciudades objetivo: ${this.config.targetCitiesPerCountry}`);
      console.log(`   ➕ Ciudades a generar: ${neededCities}`);

      // Generar ciudades en lotes
      const citiesAdded = await this.generateCitiesForCountry(country, neededCities);
      
      totalCitiesGenerated += citiesAdded;
      
      processedCountries.push({
        name: country.name,
        slug: country.slug,
        population: country.population,
        initialCities,
        finalCities: country.topCities.length,
        citiesAdded
      });

      console.log(`   ✅ ${country.name}: +${citiesAdded} ciudades (total: ${country.topCities.length})`);
    }

    const processingTime = Date.now() - startTime;

    // Resumen final
    console.log('\n' + '🎉'.repeat(25));
    console.log('🎉 MEGA EXPANSIÓN COMPLETADA');
    console.log('🎉'.repeat(25));
    console.log(`⚡ Tiempo total: ${(processingTime / 1000).toFixed(2)} segundos`);
    console.log(`🌍 Países procesados: ${eligibleCountries.length}`);
    console.log(`🏙️ Ciudades generadas: ${totalCitiesGenerated.toLocaleString()}`);
    console.log(`📈 Promedio por país: ${Math.round(totalCitiesGenerated / eligibleCountries.length)} ciudades`);

    // Mostrar top países por ciudades añadidas
    const topExpansions = processedCountries
      .filter(c => c.citiesAdded > 0)
      .sort((a, b) => b.citiesAdded - a.citiesAdded)
      .slice(0, 5);

    if (topExpansions.length > 0) {
      console.log('\n🏆 TOP 5 EXPANSIONES:');
      topExpansions.forEach((country, index) => {
        console.log(`   ${index + 1}. ${country.name}: +${country.citiesAdded.toLocaleString()} ciudades`);
      });
    }

    return {
      phase: 'MegaPopulation',
      countriesProcessed: processedCountries,
      totalCitiesGenerated,
      processingTime,
      eligibleCountries: eligibleCountries.length
    };
  }

  /**
   * Genera ciudades para un país específico
   */
  private async generateCitiesForCountry(country: WorldCountry, neededCities: number): Promise<number> {
    const existingNames = new Set<string>(country.topCities.map(city => city.name));
    const existingSlugs = new Set<string>(country.topCities.map(city => city.slug));
    
    let citiesAdded = 0;
    let startIndex = country.topCities.length + 1;

    // Generar en lotes para mejor rendimiento
    while (citiesAdded < neededCities) {
      const remaining = neededCities - citiesAdded;
      const batchSize = Math.min(this.config.batchSize, remaining);
      
      // Generar lote de ciudades
      const newCities = generateRealisticCityNames(
        country.slug,
        batchSize,
        startIndex,
        existingNames
      );

      // Validar unicidad y filtrar duplicados
      const validCities = newCities.filter(city => {
        if (existingNames.has(city.name) || existingSlugs.has(city.slug)) {
          return false;
        }
        existingNames.add(city.name);
        existingSlugs.add(city.slug);
        return true;
      });

      if (validCities.length === 0) {
        console.warn(`⚠️ No se pudieron generar más ciudades únicas para ${country.name}`);
        break;
      }

      // Generar contenido SEO si está habilitado
      if (this.config.generateSEOContent) {
        validCities.forEach(city => {
          const seoContent = generateCitySEOContent(
            city.name,
            country.name,
            country.slug,
            city.population
          );
          (city as any).seoContent = seoContent;
        });
      }

      // Añadir ciudades al país
      country.topCities.push(...validCities);
      citiesAdded += validCities.length;
      startIndex += validCities.length;

      // Log de progreso
      if (batchSize >= 10) {
        const progress = ((citiesAdded / neededCities) * 100).toFixed(1);
        console.log(`     📍 Lote completado: +${validCities.length} ciudades (${progress}%)`);
      }
    }

    return citiesAdded;
  }

  /**
   * Genera reporte detallado de la expansión
   */
  generateExpansionReport(result: PopulationExpansionResult): string {
    const report = [];
    
    report.push('📊 REPORTE DE MEGA EXPANSIÓN POBLACIONAL');
    report.push('='.repeat(50));
    report.push(`Criterio: Países con 40+ millones de habitantes`);
    report.push(`Objetivo: 1000 ciudades por país`);
    report.push(`Países elegibles: ${result.eligibleCountries}`);
    report.push(`Tiempo de procesamiento: ${(result.processingTime / 1000).toFixed(2)}s`);
    report.push('');
    
    report.push('🌍 PAÍSES PROCESADOS:');
    result.countriesProcessed.forEach((country, index) => {
      const status = country.citiesAdded > 0 ? '✅' : '⏭️';
      report.push(`${index + 1}. ${status} ${country.name}`);
      report.push(`   Población: ${country.population.toLocaleString()}`);
      report.push(`   Ciudades: ${country.initialCities} → ${country.finalCities} (+${country.citiesAdded})`);
      report.push('');
    });
    
    report.push(`🎯 TOTAL CIUDADES GENERADAS: ${result.totalCitiesGenerated.toLocaleString()}`);
    
    return report.join('\n');
  }
}

/**
 * Función de utilidad para ejecutar la mega expansión poblacional
 */
export const executeMegaPopulationExpansion = async (
  countries: WorldCountry[],
  config?: Partial<MegaPopulationConfig>
): Promise<PopulationExpansionResult> => {
  const expansion = new MegaPopulationExpansion(config);
  return await expansion.executeMegaPopulationExpansion(countries);
};

// Log de inicialización
console.log('🌍 Sistema de Mega Expansión Poblacional cargado');
console.log('📊 Criterio: Países con 40+ millones de habitantes');
console.log('🎯 Objetivo: 1000 ciudades por país elegible');
